/**
 * ThemedCombobox — generic Layer-1 combobox built on Base UI.
 * Matches the CountryCombobox visual: pill trigger, detached panel, gold checkmark.
 * searchable={false} (default) — readOnly input, all items always visible.
 * searchable={true} — typing filters items via Base UI auto-filter.
 * clearable={false} (default) — no ✕ on trigger.
 * clearable={true} — ✕ appears when value is set.
 *
 * Non-searchable: trigger auto-sizes to the longest option label via CSS grid overlay.
 */
import { useMemo } from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { CheckIcon } from "lucide-react";
import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxEmpty,
} from "@/components/ui/combobox";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/utils";

export interface ThemedComboboxOption {
  value: string;
  label: string;
  sublabel?: string;
}

export interface ThemedComboboxProps {
  options: ThemedComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  error?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Stretch the trigger to fill its container (edge-to-edge) instead of auto-sizing to the longest option. */
  fullWidth?: boolean;
  /** Render items as two-line "Custom Items" — bold label over muted sublabel (e.g. CountryCombobox). */
  stacked?: boolean;
  id?: string;
  tabIndex?: number;
  className?: string;
}

/**
 * Shared item renderer used in both searchable and non-searchable panels.
 *
 * Flow:
 * - Basic (default): single row — label, with optional muted sublabel inline.
 * - Stacked ("Custom Items"): column — bold label on top, muted sublabel below.
 *   RTL-safe via flex-col + items-start + text-start (inline-start alignment)
 *   and logical ps-8/pe-4 padding + start-3 checkmark.
 */
function OptionItem({
  opt,
  stacked = false,
}: {
  opt: ThemedComboboxOption;
  stacked?: boolean;
}) {
  return (
    <ComboboxPrimitive.Item
      value={opt}
      className={cn(
        "relative flex w-full min-h-12 cursor-default py-2 text-sm outline-hidden select-none data-highlighted:bg-[var(--card-hover)] data-highlighted:text-[var(--foreground)]",
        stacked
          ? "flex-col items-start justify-center gap-0.5 text-start ps-4 pe-9"
          : "items-center gap-2 text-start ps-4 pe-9",
      )}
    >
      {stacked ? (
        <>
          <span className="font-semibold text-[var(--foreground)] text-[14px] leading-tight">
            {opt.label}
          </span>
          {opt.sublabel && (
            <span
              className="text-xs leading-tight"
              style={{ color: "var(--text-tertiary)" }}
            >
              {opt.sublabel}
            </span>
          )}
        </>
      ) : (
        <>
          {opt.sublabel && (
            <span
              className="text-xs"
              style={{ color: "var(--text-tertiary)" }}
            >
              {opt.sublabel}
            </span>
          )}
          <span className="text-[var(--foreground)] text-[13.5px]">
            {opt.label}
          </span>
        </>
      )}
      {/* Black check at the inline-end — consistent across basic and stacked,
          matching the multi-select dropdown. Mirrors to the start edge in RTL. */}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 flex size-4 items-center justify-center" />
        }
      >
        <CheckIcon className="size-4 text-[var(--foreground)]" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

export function ThemedCombobox({
  options,
  value,
  onChange,
  placeholder,
  searchable = false,
  clearable = false,
  error = false,
  disabled = false,
  loading = false,
  fullWidth = false,
  stacked = false,
  id,
  tabIndex,
  className,
}: ThemedComboboxProps) {
  const { t } = useTranslation("common");

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  /** Longest label (including placeholder) — used to size the trigger in non-searchable mode. */
  const longestLabel = useMemo(
    () =>
      [placeholder ?? "", ...options.map((o) => o.label)].reduce(
        (a, b) => (a.length >= b.length ? a : b),
        "",
      ),
    [options, placeholder],
  );

  const panel = (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side="bottom"
        sideOffset={6}
        align="start"
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          className="group/combobox-content relative max-h-(--available-height) w-(--anchor-width) min-w-(--anchor-width) max-w-(--available-width) origin-(--transform-origin) overflow-hidden rounded-[var(--radius-input)] duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
          style={{
            background: "var(--card)",
            border: "1.5px solid var(--input-border)",
          }}
        >
          {/* ComboboxEmpty only in searchable mode — Base UI sets data-empty when no items prop,
              causing false "no results" display in static (non-searchable) panels. */}
          {searchable && <ComboboxEmpty>{t("common.noResults")}</ComboboxEmpty>}
          <ComboboxList>
            {searchable
              ? (opt: ThemedComboboxOption) => (
                  <OptionItem key={opt.value} opt={opt} stacked={stacked} />
                )
              : options.map((opt) => (
                  <OptionItem key={opt.value} opt={opt} stacked={stacked} />
                ))}
          </ComboboxList>
        </ComboboxPrimitive.Popup>
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );

  if (searchable) {
    return (
      <Combobox
        items={options}
        value={selectedOption}
        onValueChange={(opt: ThemedComboboxOption | null) =>
          onChange(opt?.value ?? null)
        }
        itemToStringLabel={(opt) => opt?.label ?? ""}
        itemToStringValue={(opt) => opt?.value ?? ""}
        autoHighlight
        disabled={disabled || loading}
      >
        <ComboboxInput
          id={id}
          placeholder={placeholder}
          showClear={clearable && !!selectedOption}
          aria-invalid={error || undefined}
          tabIndex={tabIndex}
          className={cn("bg-[var(--input-bg)]!", className)}
        />
        {panel}
      </Combobox>
    );
  }

  /**
   * Non-searchable + fullWidth: skip the auto-sizing shell, stretch the trigger
   * edge-to-edge to fill its container (matches the surrounding text fields).
   */
  if (fullWidth) {
    return (
      <div className={cn("w-full", className)}>
        <Combobox
          value={selectedOption}
          onValueChange={(opt: ThemedComboboxOption | null) =>
            onChange(opt?.value ?? null)
          }
          itemToStringLabel={(opt) => opt?.label ?? ""}
          itemToStringValue={(opt) => opt?.value ?? ""}
          disabled={disabled || loading}
        >
          <ComboboxInput
            id={id}
            readOnly
            placeholder={placeholder}
            showClear={clearable && !!selectedOption}
            aria-invalid={error || undefined}
            tabIndex={tabIndex}
            className="w-full bg-[var(--input-bg)]!"
          />
          {panel}
        </Combobox>
      </div>
    );
  }

  /**
   * Non-searchable: wrap in an inline-grid sizing shell.
   * Hidden sizer span (longestLabel) and visible input share row-start-1 col-start-1,
   * forcing the grid cell (and therefore the trigger) to always be as wide as the
   * longest option — no width change on selection.
   */
  return (
    <div className={cn("inline-grid", className)}>
      {/* Invisible width anchor — same padding/font as the input */}
      <span
        aria-hidden
        className="row-start-1 col-start-1 invisible pointer-events-none whitespace-nowrap ps-5 pe-12 py-[11px] text-[13.5px]"
      >
        {longestLabel}
      </span>
      {/* Combobox fills the same grid cell */}
      <div className="row-start-1 col-start-1">
        <Combobox
          value={selectedOption}
          onValueChange={(opt: ThemedComboboxOption | null) =>
            onChange(opt?.value ?? null)
          }
          itemToStringLabel={(opt) => opt?.label ?? ""}
          itemToStringValue={(opt) => opt?.value ?? ""}
          disabled={disabled || loading}
        >
          <ComboboxInput
            id={id}
            readOnly
            placeholder={placeholder}
            showClear={clearable && !!selectedOption}
            aria-invalid={error || undefined}
            tabIndex={tabIndex}
            className="w-full bg-[var(--input-bg)]!"
          />
          {panel}
        </Combobox>
      </div>
    </div>
  );
}
